import { ts } from '@shared/lib/diagTs';
import { getRelayOwnership } from '@core/realtime/relayOwnership';
import { allowCqgAutoResume, getCqgRelay } from './cqgRelay';
import { storeCqgCreds, type CqgEnvironment } from './cqgCreds';

/**
 * THE user-initiated CQG connect sequence, extracted from the connector dialog so every connect
 * surface (the dialog, the Add-account dialog) runs the SAME safety steps — the 2026-07-18 review
 * found that a bare `useCqgConnect().mutateAsync` silently skips them:
 *
 *  1. `allowCqgAutoResume()` — clears the terminal-rejection latch (a prior code-101 'error' blocks
 *     silent reconnects; a deliberate user connect re-arms them for THIS attempt).
 *  2. `relay.start()` — the hub must be up BEFORE the POST or the `CqgOpenSocket` push is missed.
 *  3. Stash trimmed creds + environment (the relay reads them at socket-open — the ONLY place they
 *     go; never our BE). `creds: null` skips this: take-over/refresh flows must never re-store,
 *     because a stale-mounted form would clobber freshly-stored creds (2026-07-18 review).
 *  4. The credential-less POST (rethrown to the caller for its own error UI).
 */
export async function runCqgConnectSequence(args: {
  /** Fresh creds to stash for the socket-open Logon; null → keep stored creds (take-over / refresh). */
  creds: { username: string; password: string; environment: CqgEnvironment } | null;
  takeOver?: boolean;
  /** `useCqgConnect().mutateAsync` — passed in so this stays a plain, hook-free lib function. */
  mutate: (vars: { takeOver?: boolean }) => Promise<unknown>;
}): Promise<void> {
  allowCqgAutoResume();
  // D1 (2026-09-13): an explicit Connect is a CLAIM — the tab that currently owns the CQG connection
  // closes its socket and releases before this tab starts its relay and POSTs.
  await getRelayOwnership('cqg').claim();
  try {
    await getCqgRelay().start();
  } catch (err) {
    console.error(ts(), '[cqg-connect] relay.start() failed', err);
  }
  if (args.creds) storeCqgCreds(args.creds.username.trim(), args.creds.password, args.creds.environment);
  await args.mutate({ takeOver: args.takeOver ?? false });
}
